import { useRef, useState } from 'react';
import type { DocumentSummary } from '@uit/shared/schemas/onboarding';
import s from './DocumentUploadField.module.scss';

interface Props {
  token: string;
  kind: string;
  label: string;
  required: boolean;
  existing?: DocumentSummary | undefined;
  disabled?: boolean | undefined;
  onUploaded: () => void;
}

const ACCEPT = 'application/pdf,image/png,image/jpeg';

function humanSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function messageForStatus(status: number): string {
  if (status === 413) return 'File is too large (max 10 MB).';
  if (status === 415) return 'Only PDF, PNG, or JPG files are allowed.';
  if (status === 400) return 'Could not accept that file. Check the type.';
  if (status === 409) return 'This onboarding is no longer editable.';
  return `Upload failed (${status}).`;
}

export function DocumentUploadField({
  token,
  kind,
  label,
  required,
  existing,
  disabled,
  onUploaded,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  function handleFile(file: File): void {
    setUploading(true);
    setError(null);
    setProgress(0);
    const fd = new FormData();
    fd.append('kind', kind);
    fd.append('file', file);

    // XHR rather than fetch: fetch exposes no upload progress, and phone photos
    // over a slow link take a minute or more (T-212).
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/onboard/${token}/documents`);
    xhr.withCredentials = true;
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) {
        setProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
      }
    };
    xhr.onerror = () => {
      setError('Network error. Check your connection and try again.');
      finish();
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onUploaded();
      } else {
        setError(messageForStatus(xhr.status));
      }
      finish();
    };
    xhr.send(fd);
  }

  function finish(): void {
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  }

  return (
    <div className={s.row}>
      <div className={s.meta}>
        <span className={s.label}>
          {label}
          {required && <span className={s.req}> *</span>}
        </span>
        {existing ? (
          <span className={s.fileInfo}>
            {existing.original_filename} · {humanSize(existing.size_bytes)}
          </span>
        ) : (
          <span className={s.noFile}>No file uploaded</span>
        )}
        {uploading && (
          <span className={s.progress}>
            <progress
              className={s.bar}
              max={100}
              value={progress}
              aria-label={`Uploading ${label}`}
            />
            <span className={s.pct}>{progress}%</span>
          </span>
        )}
        {error && (
          <span className={s.error} role="alert">
            {error}
          </span>
        )}
      </div>
      <div className={s.action}>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className={s.hiddenInput}
          id={`upload-${kind}`}
          disabled={disabled || uploading}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
        />
        <label
          htmlFor={`upload-${kind}`}
          className={disabled ? s.btnDisabled : s.btn}
          aria-disabled={disabled || uploading}
        >
          {uploading ? 'Uploading…' : existing ? 'Replace' : 'Upload'}
        </label>
      </div>
    </div>
  );
}
